import type AttendeeConstaint from 'owa-calendar-scheduling-service/lib/schema/AttendeeConstraint';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import {
    differenceInMinutes,
    formatDate,
    addDays,
    addHours,
    isBefore,
    OwaDate,
    owaDate,
    endOfDay,
} from 'owa-datetime';
import type EnhancedLocation from 'owa-service/lib/contract/EnhancedLocation';
import type FindMeetingTimesRequest from 'owa-calendar-scheduling-service/lib/schema/FindMeetingTimesRequest';
import type Location from 'owa-calendar-scheduling-service/lib/schema/Location';
import type LocationConstraint from 'owa-calendar-scheduling-service/lib/schema/LocationConstraint';
import type TimeConstraint from 'owa-calendar-scheduling-service/lib/schema/TimeConstraint';
import { findMeetingTimesConstants } from '../constants';
import { isValidSmtpAddress } from 'owa-recipient-email-address/lib/utils/isValidRecipientAddress';
import AttendeeRequiredStatus from 'owa-calendar-scheduling-service/lib/schema/AttendeeRequiredStatus';

const DATE_FORMAT = 'yyyy-MM-dd HH:mm:ss';

/**
 * Make the find meeting times service request and returns the response
 * @param event The event which has all the parameters
 */
export default function getFindMeetingTimeSuggestionsRequest(
    event: CalendarEvent,
    owaStartDateTime: OwaDate,
    owaEndDateTime: OwaDate
): FindMeetingTimesRequest {
    const adjustedStartDate: OwaDate = getAdjustedDate(
        addHours(owaStartDateTime, -1),
        -1 * findMeetingTimesConstants.SEARCH_WINDOW_DAYS_BEFORE
    );
    const adjustedEndDate: OwaDate = endOfDay(
        addDays(adjustedStartDate, findMeetingTimesConstants.SEARCH_WINDOW_DAYS_AFTER)
    );

    return getBaseFindMeetingTimesSuggestionsRequest(
        event,
        adjustedStartDate,
        adjustedEndDate,
        owaStartDateTime.tz, // uses the same timezone as start date to make sure the length of the search window is the one specified
        getDuration(owaStartDateTime, owaEndDateTime),
        findMeetingTimesConstants.MINIMUM_ATTENDEE_PERCENTAGE,
        findMeetingTimesConstants.MAX_CANDIDATES,
        true /* includeLocationConstraint */
    );
}

/**
 * Make the find meeting times service request for reschedule scenario
 * @param event - The calendar event which has all the parameters
 * @param searchingStartTime - start time for the searching window
 * @param searchingEndTime - end time for the searching window
 */
export function getFindMeetingTimesSuggestionsRequestForReschedule(
    event: CalendarEvent,
    searchingStartTime: OwaDate,
    searchingEndTime: OwaDate,
    duration: number
) {
    return getBaseFindMeetingTimesSuggestionsRequest(
        event,
        searchingStartTime,
        searchingEndTime,
        searchingStartTime.tz,
        getISO8601FormatDuration(duration),
        findMeetingTimesConstants.MINIMUM_ATTENDEE_PERCENTAGE,
        findMeetingTimesConstants.MAX_CANDIDATES /* maxCandidates */
    );
}

/**
 * Make the find meeting times service request and returns the response
 * @param event - The calendar event which has all the parameters
 * @param searchingStartTime - start time for the searching window
 * @param searchingEndTime - end time for the searching window
 * @param searchingTimeZone - time zone for the searching window
 * @param duration - duration for the time suggestions
 * @param minimumAttendeePercentage - minimum attendee percentage for the time suggestions
 * @param maxCandidates - maximum number of time suggestion being returned
 * @param includeLocationConstraint - should include location constration or not
 */
function getBaseFindMeetingTimesSuggestionsRequest(
    event: CalendarEvent,
    searchingStartTime: OwaDate,
    searchingEndTime: OwaDate,
    searchingTimeZone: string,
    duration: string /* In ISO8601 format */,
    minimumAttendeePercentage: number,
    maxCandidates: number,
    includeLocationConstraint: boolean = false
) {
    return {
        attendees: [
            ...event.RequiredAttendees.map(value =>
                attendeeTypeToAttendeeConstraint(value, AttendeeRequiredStatus.Required)
            ).filter(attendee => attendee != null),
            ...event.OptionalAttendees.map(value =>
                attendeeTypeToAttendeeConstraint(value, AttendeeRequiredStatus.Optional)
            ).filter(attendee => attendee != null),
        ],
        timeConstraint: {
            timeslots: [
                {
                    Start: {
                        DateTime: formatDate(searchingStartTime, DATE_FORMAT),
                        TimeZone: searchingTimeZone,
                    },
                    End: {
                        DateTime: formatDate(searchingEndTime, DATE_FORMAT),

                        TimeZone: searchingTimeZone,
                    },
                },
            ],
        } as TimeConstraint,
        meetingDuration: duration,
        minimumAttendeePercentage: minimumAttendeePercentage,
        maxCandidates: maxCandidates,
        locationConstraint: includeLocationConstraint
            ? createLocationConstraint(event.Locations)
            : null,
    } as FindMeetingTimesRequest;
}

/**
 * Converts the AttendeeType into AttendeeConstraint
 * @param attendee Attendee as AttendeeType
 */
function attendeeTypeToAttendeeConstraint(
    attendee: AttendeeType,
    attendeeRequiredStatus: AttendeeRequiredStatus
): AttendeeConstaint {
    if (!isValidSmtpAddress(attendee.Mailbox.EmailAddress)) {
        // If the email address is invalid (for example, when it is an unresolved attendee in the well), then return null
        return null;
    }

    return {
        emailAddress: {
            address: attendee.Mailbox.EmailAddress,
        },
        type: attendeeRequiredStatus,
    } as AttendeeConstaint;
}

/**
 * Return the location constraint
 */
function createLocationConstraint(locations: EnhancedLocation[]): LocationConstraint {
    if (!locations) {
        return { suggestLocation: false };
    }

    let rooms = locations.filter(
        location => location?.PostalAddress && location.PostalAddress.LocationSource == 'Resource'
    );

    let roomsAsLocation: Location[] = rooms.map(
        room => ({ email: room.PostalAddress.LocationUri, name: room.DisplayName } as Location)
    );

    return { locations: roomsAsLocation, suggestLocation: false, isRequired: true };
}

/**
 * Adjust the date by adding the 'days'.
 * If the date after adding 'days' is in the past, then return the current time,
 * else return the adjusted date
 */
function getAdjustedDate(date: OwaDate, days: number) {
    let dateAfterDays = addDays(date, days);
    let currentTime = owaDate();
    if (isBefore(currentTime, dateAfterDays)) {
        // dateAfterDays is in the future return dateAfterDays
        return dateAfterDays;
    } else {
        // if dateAfterDays is in the past, return current time
        return owaDate(date.tz, currentTime);
    }
}

/**
 * Returns the duration between the start/end in ISO8601 format.
 */
function getDuration(start: OwaDate, end: OwaDate): string {
    // Convert end to the same timezone as the start
    end = owaDate(start.tz, end);
    const diff = differenceInMinutes(end, start);

    return getISO8601FormatDuration(diff);
}

function getISO8601FormatDuration(duration: number): string {
    return 'PT' + duration + 'M';
}
